import {
  Component,
  ChangeDetectionStrategy,
  inject,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  effect,
  Signal,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserService } from '../services/user.service';
import { User } from '../services/user.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('appearAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate(
          '500ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class UsersListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('headerVideo1') vH1!: ElementRef<HTMLVideoElement>;
  @ViewChild('headerVideo2') vH2!: ElementRef<HTMLVideoElement>;
  @ViewChild('bodyVideo1') vB1!: ElementRef<HTMLVideoElement>;
  @ViewChild('bodyVideo2') vB2!: ElementRef<HTMLVideoElement>;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef<HTMLDivElement>;

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private readonly userService = inject(UserService);
  private readonly zone = inject(NgZone);

  public readonly users: Signal<User[]> = this.userService.getUsers();
  public readonly giveawayData = this.userService.getGiveawayData();
private animationId: number = 0;
  private scrollInterval: number | null = null;
  private readonly scrollSpeed = 1;
  private readonly scrollIntervalMs = 16;

  constructor() {
    this.registerCustomIcons();
    this.userService.startUsersPolling();
    this.setupUserListSubscription();
  }

  private registerCustomIcons() {
    this.iconRegistry.addSvgIcon('twitch', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/twitch.svg'));
    this.iconRegistry.addSvgIcon('youtube', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/youtube.svg'));
  }

  getUserIcon(user: User): string {
    return user.is_youtube ? 'youtube' : 'twitch';
  }

  getHeader(): string {
    const data = this.giveawayData();
    if (!data) return '';
    if (data.state === 'idle') return 'Giveaway starting shortly!';
    if (data.state === 'countdown') {
      const seconds = Math.ceil((data.start_time! * 1000 - new Date().getTime()) / 1000);
      return `Giveaway starts in ${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
    return data.state === 'winners' ? 'WINNERS!' : `Type: ${data.keyword || ''} to enter`;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      // Даем небольшую задержку, чтобы DOM стабилизировался
      setTimeout(() => {
        this.initSeamlessLoop(this.vH1?.nativeElement, this.vH2?.nativeElement);
        this.initSeamlessLoop(this.vB1?.nativeElement, this.vB2?.nativeElement);
      }, 100);
    });
  }

private initSeamlessLoop(v1: HTMLVideoElement, v2: HTMLVideoElement) {
  if (!v1 || !v2) return;

  const overlapTime = 5.5; 
  const crossfadeTime = 4.5;
  let isPreparingNext = false; // Флаг, чтобы не спамить .play()

  const setupVideo = (v: HTMLVideoElement) => {
    v.muted = true;
    v.loop = false;
    v.preload = 'auto';
  };

  setupVideo(v1);
  setupVideo(v2);

  const checkProgress = () => {
    const current = v1.classList.contains('active') ? v1 : v2;
    const next = current === v1 ? v2 : v1;

    // 1. Подготовка: запускаем "невидимое" видео заранее
    if (current.currentTime > current.duration - overlapTime && !isPreparingNext) {
      isPreparingNext = true;
      next.currentTime = 0;
      next.play().then(() => {
        console.log('Next video started in background');
      }).catch(e => {
        isPreparingNext = false;
        console.warn("Pre-roll failed", e);
      });
    }

    // 2. Смена ролей: делаем "невидимое" видео активным
    if (current.currentTime > current.duration - crossfadeTime && isPreparingNext && !next.classList.contains('active')) {
      next.classList.add('active');
      current.classList.remove('active');
      
      // Через время кроссфейда сбрасываем флаг подготовки для следующего цикла
      setTimeout(() => {
        current.pause();
        isPreparingNext = false; 
      }, crossfadeTime * 1000);
    }

    this.animationId = requestAnimationFrame(checkProgress);
  };

  // Стартовое состояние
  v1.classList.add('active');
  v1.play().then(() => {
    this.zone.runOutsideAngular(() => checkProgress());
  });
}

  private setupUserListSubscription(): void {
    effect(() => {
      if (this.users().length > 13) this.startAutoScroll();
      else this.stopAutoScroll();
    });
  }

  private startAutoScroll(): void {
    if (this.scrollInterval !== null || !this.scrollContainer) return;
    this.zone.runOutsideAngular(() => {
      this.scrollInterval = window.setInterval(() => {
        const element = this.scrollContainer.nativeElement;
        element.scrollTop += this.scrollSpeed;
        if (element.scrollTop + element.clientHeight >= element.scrollHeight - 40) {
          this.zone.run(() => this.userService.duplicateUsers());
        }
      }, this.scrollIntervalMs);
    });
  }

  private stopAutoScroll(): void {
    if (this.scrollInterval !== null) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  trackByUserId(index: number, user: User) { return user.id; }

  ngOnDestroy(): void {
    this.stopAutoScroll();
    this.userService.stopUsersPolling();
    [this.vH1, this.vH2, this.vB1, this.vB2].forEach(v => {
      if (v?.nativeElement) {
        v.nativeElement.ontimeupdate = null;
        v.nativeElement.pause();
        v.nativeElement.src = "";
      }
    });
  }
}